import React, { useState, useMemo, useEffect } from 'react';
import useApiContext from 'hooks/useApiContext';
import useModalContext from 'hooks/useModalContext';
import useRepo from 'hooks/useRepo';
import { T, M, A } from 'TOMA';
import s from './CategoriesList.module.scss';
import routes from 'router/routes';
import { toast } from 'react-toastify';
import { CustomLink } from 'components/CustomLink';
import { Controller } from 'react-hook-form';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { eRoles } from '../../interfaces/Roles';
import HierarchyImport from 'components/HierarchyImport';
import { useTranslation } from 'react-i18next';

const columnHelper = createColumnHelper<any>();

function Categories(): JSX.Element {
  const { setModalContent, setModalVisible } = useModalContext();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const { userRepository, categoryRepository } = useApiContext();
  const user = userRepository.getUser();
  const isAdmin =
    user?.role === eRoles.ADMIN || user?.role === eRoles.SUPER_ADMIN;
  const [reload, setReload] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<
    number | null
  >(null);

  const {
    response = [],
    loading: dataLoading,
    error,
  } = useRepo(async () => {
    return await categoryRepository.getAll(
      'category',
      selectedParentCategoryId ?? 0
    );
  }, [reload, selectedParentCategoryId]);

  const { t } = useTranslation();

  const handleParentCategory = async (categoryId: number): Promise<void> => {
    setSelectedParentCategoryId(categoryId);
    // The table will automatically update via the useRepo dependency array
  };

  const [categories, setCategories] = useState<any[]>([]);
  const { response: parentCategories } = useRepo(async () => {
    return await categoryRepository.parentCategory('universe');
  }, []);

  useEffect(() => {
    if (parentCategories) {
      setCategories(parentCategories);
    }
  }, [parentCategories]);

  const handleDelete = async (categoryId: string): Promise<void> => {
    setModalContent(
      <div>
        <A.Text fontSize="22" fw="bold" mb="20">
          {t('categories:confirm_delete')}
        </A.Text>
        <T.Flex gap={2} valign="center" mb="-15">
          <A.Button
            bg="alert"
            onClick={async () => {
              try {
                await categoryRepository.deleteCategory(categoryId);
                toast.success(t('categories:delete_success'));
                setModalVisible(false);
                setReload((e) => e + 1);
              } catch (error: any) {
                toast.error(error.message || t('categories:delete_error'));
              }
            }}
          >
            {t('common:confirm')}
          </A.Button>
          <A.Button
            appearance="link"
            color="grey-80"
            mb="15"
            textDecoration="underline"
            onClick={() => setModalVisible(false)}
          >
            {t('common:cancel')}
          </A.Button>
        </T.Flex>
      </div>
    );
    setModalVisible(true);
  };

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('topicCode', {
        header: t('categories:topic_code'),
        size: 200,
        cell: (info) => {
          const val = info.getValue();
          return (
            <div
              className={s.ExhibitorList__status}
              data-key={val}
              style={{ textAlign: 'center' }}
            >
              {val}
            </div>
          );
        },
      }),
      columnHelper.accessor('code', {
        header: t('categories:code'),
        size: 100,
        cell: (info) => {
          const val = info.getValue();
          return (
            <div
              className={s.ExhibitorList__status}
              data-key={val}
              style={{ textAlign: 'center' }}
            >
              {val}
            </div>
          );
        },
      }),
      columnHelper.accessor('parent', {
        header: t('categories:parent'),

        cell: (info) => {
          const val = info.getValue();
          return (
            <div
              className={s.ExhibitorList__status}
              data-key={val}
              style={{ textAlign: 'center' }}
            >
              {val?.label || '-'}
            </div>
          );
        },
      }),
      ...(isAdmin
        ? [
            columnHelper.accessor('actions', {
              header: t('common:actions'),
              enableSorting: false,
              cell: ({ row }) => {
                const category = row.original;
                return (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <T.Flex gap={1}>
                      <CustomLink
                        to={routes.home2.path + `categories/${category.id}`}
                        color="info"
                      >
                        <A.Icon icon="pencil" color="info" fontSize="18" />
                      </CustomLink>
                      <button
                        type="button"
                        className={s.ExhibitorList__remove}
                        onClick={async () => await handleDelete(category.id)}
                      >
                        <A.Icon icon="trash2" color="alert" fontSize="18" />
                      </button>
                    </T.Flex>
                  </div>
                );
              },
            }),
          ]
        : []),
    ];

    return baseColumns;
  }, [response, isAdmin, t]);

  const data = useMemo(
    () =>
      response?.filter((category: any) => {
        if (!globalFilter) return true;
        const searchLower = globalFilter.toLowerCase();
        return (
          category.topicCode?.toLowerCase().includes(searchLower) ||
          category.code?.toLowerCase().includes(searchLower)
        );
      }) || [],
    [response, globalFilter]
  );

  // const handleFilterChange = (value: any): void => {
  //   console.log('Filter value changed:', value);
  // };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Show form modal when needed
  useEffect(() => {
    if (showForm) {
      setModalVisible(true);
    } else {
      setModalVisible(false);
    }
  }, [showForm, selectedCategory]);
  return (
    <div>
      <T.Container>
        <M.Loading loading={dataLoading} error={error} fullWiewport>
          <A.Title
            as="h1"
            fontSize="48"
            mb="30"
            mt="50"
            fw="black"
            textAlign="center"
          >
            {t('categories:categories_management')}
          </A.Title>

          <HierarchyImport onImported={() => setReload((e) => e + 1)} />

          {/* Header with Add Button and Search */}
          <T.Flex halign="space-between" valign="center" mb="20">
            <T.Block className={s.right}>
              <A.Button>
                <CustomLink
                  to={routes.home2.path + `categories/new-category`}
                  color="white"
                >
                  {t('categories:create')}
                </CustomLink>
              </A.Button>
            </T.Block>
            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ width: '300px' }}>
                <A.InputAutocomplete
                  name="parent_id"
                  placeholder={t('categories:select_parent_category')}
                  value={
                    selectedParentCategoryId
                      ? {
                          value: selectedParentCategoryId.toString(),
                          label:
                            categories?.find(
                              (cat) => cat.id === selectedParentCategoryId
                            )?.label || '',
                        }
                      : null
                  }
                  onChange={(value: any) => {
                    if (value) {
                      handleParentCategory(parseInt(value.value));
                    } else {
                      setSelectedParentCategoryId(null);
                    }
                  }}
                  options={[
                    ...(categories?.map((item) => {
                      return {
                        value: item.id.toString(),
                        label: item.label,
                      };
                    }) || []),
                  ]}
                />
              </div>
              <div style={{ width: '300px' }}>
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder={t('categories:search_placeholder')}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    height: '40px',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          </T.Flex>

          {/* Categories Table */}
          <T.Block mb="20">
            <table className={s.Table}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        <A.Button
                          appearance="link"
                          color="grey-80"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          {{
                            asc: <A.Icon icon="chevron-down" ml="5" />,
                            desc: <A.Icon icon="chevron-up" ml="5" />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </A.Button>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, index) => (
                  <tr
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#F8F8F8',
                    }}
                    key={row.id}
                    className={s.ExhibitorList__row}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                      >
                        <div className={s.ExhibitorList__row__div}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </T.Block>

          {data.length === 0 && !dataLoading && (
            <div style={{ textAlign: 'center', paddingTop: '40px' }}>
              <A.Text fontSize="18" color="grey-60">
                {globalFilter
                  ? t('categories:no_results')
                  : t('categories:no_categories')}
              </A.Text>
            </div>
          )}
        </M.Loading>
      </T.Container>
    </div>
  );
}

export default Categories;
